import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Upload, SlidersHorizontal, Users, UserCheck, UserMinus, Plus, Pill, Syringe, Send, PackageCheck, HeartPulse } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { DateRange, initialRange, Select } from "@/components/common";
import { PatientTable } from "@/components/PatientTable";

const pct = (v) => (v == null ? "N/A" : `${v}%`);

function Mini({ label, value, hint, testid, onClick, bar, color = "#283F91" }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`min-w-0 rounded-md bg-[var(--surface)] px-2.5 py-1.5 text-left transition-colors ${onClick ? "hover:bg-blue-50 dark:hover:bg-white/10" : "cursor-default"}`}>
      <div className="truncate text-[10px] font-semibold uppercase tracking-wide text-[var(--muted-ink)]" title={label}>{label}</div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-lg font-bold leading-tight text-[var(--ink)]" data-testid={testid ? `${testid}-value` : undefined}>{value ?? "—"}</span>
        {hint && <span className="truncate text-[10px] text-[var(--muted-ink)]" title={hint}>{hint}</span>}
      </div>
      {bar != null && <div className="mt-1 h-1 rounded bg-[hsl(var(--border))]"><div className="h-1 rounded" style={{ width: `${Math.min(100, bar)}%`, background: color }} /></div>}
    </button>
  );
}

function Section({ title, cols, children, testid, span }) {
  return (
    <section className={`panel p-2.5 ${span || ""}`} data-testid={testid}>
      <h2 className="mb-1.5 px-0.5 text-[11px] font-bold uppercase tracking-wider text-[#283F91] dark:text-blue-300">{title}</h2>
      <div className={`grid gap-1.5 ${cols}`}>{children}</div>
    </section>
  );
}

const PREP_MEDS = ["Descovy", "Apretude", "Yeztugo", "Generic Truvada", "Truvada"];
const TX_MEDS = ["Cabenuva", "Biktarvy", "Dovato"];
const slug = (m) => m.toLowerCase().replace(/\s+/g, "-");

function MedCard({ name, count, injectable, tx, onClick }) {
  const Icon = injectable ? Syringe : Pill;
  return (
    <button type="button" onClick={onClick} data-testid={`card-med-${slug(name)}`}
      className={`flex min-w-0 items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-colors hover:border-[#283F91]/40 dark:!bg-white/5 ${tx ? "border-slate-200 bg-slate-50" : "border-blue-100 bg-[#F3F8FF]"}`}>
      <span className={`rounded-md p-1.5 ${tx ? "bg-slate-200/70 text-slate-600" : "bg-white text-[#283F91]"} dark:bg-white/10 dark:text-blue-200`}><Icon size={13} /></span>
      <span className="min-w-0">
        <span className="block truncate text-[10px] font-semibold uppercase tracking-wide text-[var(--muted-ink)]" title={name}>{name}</span>
        <span className="block text-lg font-bold leading-tight text-[var(--ink)]" data-testid={`card-med-${slug(name)}-value`}>{count}</span>
      </span>
    </button>
  );
}

function MedGroup({ label, names, bm, types, tx, nav, testid }) {
  return (
    <div data-testid={testid}>
      <div className="mb-1 px-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--muted-ink)]">{label}</div>
      <div className={`grid gap-1.5 ${tx ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"}`}>
        {names.map((m) => <MedCard key={m} name={m} count={bm[m] || 0} injectable={types[m] === "injectable"} tx={tx} onClick={() => nav(`/patients?medication=${encodeURIComponent(m)}`)} />)}
      </div>
    </div>
  );
}

function BigStat({ icon: Icon, label, value, hint, testid }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-blue-100 bg-[#F7FAFF] px-3 py-2 dark:border-white/10 dark:bg-white/5" data-testid={testid}>
      <span className="rounded-md bg-white p-2 text-[#283F91] dark:bg-white/10 dark:text-blue-200"><Icon size={15} /></span>
      <span className="min-w-0">
        <span className="block text-[10px] font-semibold uppercase tracking-wide text-[var(--muted-ink)]">{label}</span>
        <span className="block text-xl font-bold leading-tight text-[var(--ink)]" data-testid={`${testid}-value`}>{value ?? 0}</span>
        {hint && <span className="block truncate text-[10px] text-[var(--muted-ink)]">{hint}</span>}
      </span>
    </div>
  );
}

function Filters({ f, setF }) {
  const { meta, user } = useApp();
  return (
    <div className="mb-3 grid grid-cols-2 gap-1.5 md:grid-cols-4 xl:grid-cols-7" data-testid="dashboard-filters">
      <Select className="h-8 text-[13px]" value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} placeholder="All navigators" testid="dash-filter-employee" options={(meta?.staff || []).map((s) => ({ value: s.id, label: s.name }))} />
      {user.role === "admin" && <Select className="h-8 text-[13px]" value={f.supervisor_id} onChange={(v) => setF({ ...f, supervisor_id: v })} placeholder="All supervisors" testid="dash-filter-supervisor" options={(meta?.supervisors || []).map((s) => ({ value: s.id, label: s.name }))} />}
      <Select className="h-8 text-[13px]" value={f.location_id} onChange={(v) => setF({ ...f, location_id: v })} placeholder="All locations" testid="dash-filter-location" options={(meta?.locations || []).map((l) => ({ value: l.id, label: l.name }))} />
      <Select className="h-8 text-[13px]" value={f.medication} onChange={(v) => setF({ ...f, medication: v })} placeholder="All medications" testid="dash-filter-medication" options={(meta?.medications || []).map((m) => m.name)} />
      <input className="inp h-8 text-[13px]" placeholder="Pharmacy" value={f.pharmacy || ""} onChange={(e) => setF({ ...f, pharmacy: e.target.value })} data-testid="dash-filter-pharmacy" />
      <input className="inp h-8 text-[13px]" placeholder="Insurance" value={f.insurance || ""} onChange={(e) => setF({ ...f, insurance: e.target.value })} data-testid="dash-filter-insurance" />
      <Select className="h-8 text-[13px]" value={f.status} onChange={(v) => setF({ ...f, status: v })} placeholder="All statuses" testid="dash-filter-status" options={meta?.statuses || []} />
    </div>
  );
}

export default function Dashboard({ mine }) {
  const { user, t, can, meta } = useApp();
  const nav = useNavigate();
  const types = Object.fromEntries((meta?.medications || []).map((m) => [m.name, m.type]));
  const [range, setRange] = useState(initialRange());
  const [f, setF] = useState({});
  const [showF, setShowF] = useState(false);
  const [d, setD] = useState(null);
  const [rk, setRk] = useState(0);
  useEffect(() => {
    const params = { start: range.start, end: range.end, view: mine ? "mine" : undefined, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) };
    api.get("/dashboard", { params }).then((r) => setD(r.data)).catch(() => {});
  }, [range, f, rk, mine]);
  const s = d?.summary;
  const bm = s?.by_medication || {};
  const title = mine ? "My Dashboard" : { admin: "Admin Dashboard", supervisor: "Team Dashboard", employee: "Dashboard" }[user.role];
  return (
    <div>
      <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-bold tracking-tight text-[var(--ink)] md:text-2xl" data-testid="page-title">{t(title)}</h1>
          <span className="text-xs text-[var(--muted-ink)]">{mine || user.role === "employee" ? "Your assigned caseload" : user.role === "supervisor" ? "Your team" : "Organization-wide"}</span>
        </div>
        <div className="no-print flex flex-wrap items-center gap-2">
          <DateRange value={range} onChange={setRange} />
          {user.role !== "employee" && <button className={`btn btn-sm ${showF ? "btn-primary" : "btn-ghost"}`} onClick={() => setShowF(!showF)} data-testid="toggle-filters-btn"><SlidersHorizontal size={13} />{t("Filters")}</button>}
          {can("action:patient_create") && <button className="btn btn-primary btn-sm" onClick={() => nav("/patients/new")} data-testid="dash-add-patient-btn"><UserPlus size={13} />{t("Add New Patient")}</button>}
          {can("action:import") && <button className="btn btn-ghost btn-sm" onClick={() => nav("/import")} data-testid="dash-import-btn"><Upload size={13} />{t("Import Patients")}</button>}
        </div>
      </div>
      {showF && user.role !== "employee" && <Filters f={f} setF={setF} />}
      {s && (
        <div className="mb-2.5 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4" data-testid="status-cards">
          {[["card-total-clients", "Total PrEP Clients", s.total_clients, "#EAF4FF", "#BFDBFE", Users, ""],
            ["card-active-clients", "Active PrEP Clients", s.active_clients, "#E8F8EF", "#BBF7D0", UserCheck, "status=ACTIVE"],
            ["card-discharged-clients", "Discharged Patients", s.discharged_clients, "#FDECEC", "#FECACA", UserMinus, "status=DISCHARGED"],
            ["card-hiv-patients", "Total HIV Patients", s.treatment_clients, "#F3EEFF", "#DDD6FE", HeartPulse, ""]].map(([id, label, v, bg, bd, Icon, q]) => (
            <button key={id} data-testid={id} onClick={() => nav(`/patients${q ? `?${q}` : ""}`)} style={{ background: bg, borderColor: bd }}
              className="flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-shadow hover:shadow-sm dark:!bg-white/5">
              <span className="rounded-lg bg-white/80 p-2 text-[#20336F] dark:bg-white/10 dark:text-blue-200"><Icon size={18} /></span>
              <span>
                <span className="block text-[11px] font-semibold uppercase tracking-wide text-[#303034] dark:text-gray-300">{label}</span>
                <span className="block text-2xl font-bold leading-tight text-[#20336F] dark:text-white" data-testid={`${id}-value`}>{v ?? 0}</span>
                {id === "card-total-clients" && s.total_clients - s.active_clients - s.discharged_clients > 0 && (
                  <span className="block text-[10px] text-[var(--muted-ink)]" data-testid="legacy-status-hint">incl. {s.total_clients - s.active_clients - s.discharged_clients} with other statuses</span>)}
              </span>
            </button>
          ))}
        </div>
      )}
      <div className="mb-4 space-y-2.5" data-testid="dashboard-summary">
        {!s ? <div className="panel p-4 text-sm text-[var(--muted-ink)]">Loading…</div> : (
          <>
            <section className="panel p-2.5" data-testid="summary-patients">
              <h2 className="mb-1.5 px-0.5 text-[11px] font-bold uppercase tracking-wider text-[#283F91] dark:text-blue-300">{t("Patient totals by medication")}</h2>
              <div className="grid gap-2.5 lg:grid-cols-[5fr_3fr]">
                <MedGroup label="PrEP medications" names={PREP_MEDS} bm={bm} types={types} nav={nav} testid="med-group-prep" />
                <MedGroup label="HIV treatment (not counted as PrEP clients)" names={TX_MEDS} bm={bm} types={types} tx nav={nav} testid="med-group-treatment" />
              </div>
            </section>
            <div className="grid gap-2.5 lg:grid-cols-2">
            <Section title={t("Prescription totals")} cols="grid-cols-1 sm:grid-cols-2" span="h-full" testid="summary-prescriptions">
              <BigStat icon={Send} testid="card-rx-sent" label="Prescriptions Sent" value={s.rx_sent} hint="Prescription records sent in the selected period" />
              <BigStat icon={PackageCheck} testid="card-equivalent" label="Total Recorded Rx Fills" value={s.equivalent_fills} hint="Each saved fill date = 1 fill, all medications" />
            </Section>
            <Section title="Retention & Adherence" cols="grid-cols-1 sm:grid-cols-2" span="h-full" testid="summary-retention">
              <Mini testid="card-retention" label="PrEP Care Retention Rate" value={pct(s.retention.rate)} hint={`${s.retention.retained}/${s.retention.eligible} eligible${s.retention.insufficient ? ` · ${s.retention.insufficient} insufficient data` : ""}`} bar={s.retention.rate} color="#16a34a" />
              <Mini testid="card-adherence" label="Prescription Fill Adherence Rate" value={pct(s.adherence.rate)} hint={`${s.adherence.completed}/${s.adherence.expected} expected months covered${s.adherence.insufficient ? ` · ${s.adherence.insufficient} insufficient data` : ""}`} bar={s.adherence.rate} />
            </Section>
            </div>
          </>
        )}
      </div>
      {can("action:patient_create") && (
        <div className="no-print mb-2 flex justify-end">
          <button className="btn btn-primary btn-sm" onClick={() => nav("/patients/new?return=dashboard")} data-testid="patient-list-add-btn"><Plus size={13} />Add Patient</button>
        </div>
      )}
      <PatientTable key={mine ? "mine" : "team"} view={mine ? "mine" : undefined} onChanged={() => setRk((x) => x + 1)} />
    </div>
  );
}
